import { Avatar, Box, Button, Card, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography, useTheme } from '@mui/material'
import { Bar, BarChart, CartesianGrid, Legend, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import LogsTableRow from '../../components/admin/logging/LogsTableRow'
import BlockMessage from '../../components/common/BlockMessage'
import TableHeader from '../../components/common/TableHeader'
import { formatDate, Icons } from '../../utils/utilities'
import { useUserStore } from '../../store/user.store'
import noLogsImg from '../../assets/no-logs.svg'
import api from '../../service/api'
import FilterBar from '../../components/admin/logging/FilterBar'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineOppositeContent, {
    timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent'
import LogBlock from '../../components/admin/logging/LogBlock'

const tableColumns = [
    { name: "Service", padding: "5px 10px" },
    { name: "Level", minWidth: "100px", padding: "5px 10px" },
    { name: "time", padding: "5px 10px" },
    { name: "Type", padding: "5px 10px" },
    { name: "Message", minWidth: 500, padding: "5px 10px" },
]

const DebugLogs = () => {

    const [page, setPage] = useState( 0 )
    const [count, setCount] = useState( 100 )
    const [logs, setLogs] = useState( [] )
    const [stats, setStats] = useState( {} )
    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [rowsPerPage, setRowsPerPage] = useState( 50 )
    const [loadingMore, setLoadingMore] = useState( false )
    const [loadingStats, setLoadingStats] = useState( false )
    const [hasPermission, setHasPermission] = useState( true )
    const [availableModules, setAvailableModules] = useState( [] )

    const UserStore = useUserStore()

    const [searchParams, setSearchParams] = useSearchParams( { filterLevel: "error", view: "logs" } )
    const filterUserId = searchParams.get( "filterUserId" ) || ""
    const filterDate = searchParams.get( "filterDate" ) || ""
    const filterModule = searchParams.get( "filterModule" ) || ""
    const filterLevel = searchParams.get( "filterLevel" ) || ""
    const view = searchParams.get( "view" ) || "logs"

    const statsView = useMemo( () => view === 'stats', [view] )

    const filtersApplied = Boolean( filterDate || filterUserId || filterLevel || filterModule )

    const { palette, border, table } = useTheme()
    const fetchedLogsCount = useRef( 0 )

    const removeFilters = () => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( 'filterDate', '' )
            newParams.set( 'filterUserId', '' )
            newParams.set( 'filterLevel', '' )
            newParams.set( 'filterModule', '' )
            return newParams
        }, { replace: true } )
    }

    const setStatsView = useCallback( state => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( 'view', state ? 'stats' : 'logs' )
            return newParams
        }, { replace: true } )
    }, [setSearchParams] )

    const logFilterFunc = useCallback( item => {
        if ( item.rowType === 'date' ) return true
        return item?.meta?.module?.match( filterModule ) && item?.level?.match( filterLevel )
    }, [filterModule, filterLevel] )

    const handleCountChange = action => {
        if ( action === '+' ) {
            if ( count <= 400 )
                setCount( prev => prev + 100 )
        } else if ( action === '-' ) {
            if ( count > 100 )
                setCount( prev => prev - 100 )
        }
    }

    const filteredLogs = useMemo( () => {
        return logs.filter( logFilterFunc )
    }, [logs, logFilterFunc] )

    const switchMode = async newMode => {
        if ( newMode === 'stats' ) {
            if ( !stats || Object.keys( stats ).length === 0 ) {
                await fetchStats()
            } else {
                setStatsView( true )
            }
        } else {
            setStatsView( false )
        }
    }

    const refreshData = () => {
        if ( statsView )
            fetchStats()
        else
            getData()
    }

    const fetchStats = useCallback( async () => {
        try {
            const filters = {}
            if ( !filterDate || ( filterDate && filterDate.match( /^(\d{4}-\d{2}-\d{2})$/ ) ) )
                filters.filterDate = filterDate || ""
            setLoadingStats( true )
            const { data } = await api.logging.fetchServiceLogsStats( filters )
            const nullErrors = data.counts?.findIndex( err => err._id + "" === "null" )
            if ( nullErrors >= 0 ) {
                const serverErrors = data.counts?.find( err => +err._id === 500 )
                if ( serverErrors ) {
                    serverErrors.count += data.counts[nullErrors].count
                } else {
                    data.counts.push( { _id: 500, count: nullErrors.count } )
                }
                data.counts.splice( nullErrors, 0 )
            }
            for ( const item of data.errorsByModule ) {
                item.module = String( item._id ).replace( /_/g, " " ).toLowerCase()
                const mod = String( item._id ).replace( /_/g, " " )
                const words = mod.split( " " )
                let shortCode = ''
                if ( words.length > 1 ) {
                    shortCode = words.map( w => w[0] ).join( "" )
                } else {
                    shortCode = mod.substring( 0, 4 )
                }
                item.shortCode = shortCode
            }
            setStats( data )
            setStatsView( true )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong" } )
        } finally {
            setLoadingStats( false )
        }
    }, [filterDate, setStatsView] )

    const getData = useCallback( async ( loadingMore = false ) => {
        try {
            if ( loadingMore )
                setLoadingMore( true )
            else
                setLoading( true )

            const filters = {}
            if ( filterDate && filterDate.match( /^(\d{4}-\d{2}-\d{2})$/ ) ) filters.filterDate = filterDate
            if ( filterUserId ) filters.filterUserId = filterUserId
            if ( filterLevel ) filters.filterLevel = filterLevel
            filters.count = count
            if ( loadingMore )
                filters.skipBatchCount = Math.ceil( fetchedLogsCount.current / count )

            const { data: { logs: data, uniqueModules } } = await api.logging.fetchServiceLogs( filters )
            setError( null )
            setLogs( data || [] )
            setAvailableModules( uniqueModules )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong" } )
        } finally {
            setLoading( false )
            setLoadingMore( false )
        }
    }, [filterDate, filterUserId, filterLevel, count] )

    useEffect( () => {
        setPage( 0 )
    }, [filterDate, filterLevel, filterModule, filterUserId] )

    useEffect( () => {
        if ( UserStore.getUser.user_role === 'subadmin' && !UserStore.getUser.user_permissions.activity_log ) {
            setHasPermission( false )
        } else {
            if ( view === 'stats' )
                fetchStats()
            else
                getData()
        }
    }, [UserStore, view, getData, fetchStats] )


    return (
        hasPermission ? (
            <Box display="flex" flexDirection="column" overflow="auto" flexGrow={1} padding={{ xs: "10px", md: "20px" }}>
                <Paper
                    elevation={statsView ? 0 : 1}
                    sx={{
                        background: statsView && "none",
                        minHeight: !statsView && filteredLogs.length > 0 && !loading && !error ? "450px" : "initial",
                        display: "flex",
                        flexGrow: !statsView && filteredLogs.length > 0 && !loading && !error && !statsView ? 1 : "initial",
                        height: !statsView && filteredLogs.length > 0 && !loading && !error ? "100%" : "initial",
                        flexDirection: "column"
                    }}>
                    <Card elevation={statsView ? 1 : 0} sx={{ borderRadius: statsView ? "10px" : "0", flexShrink: 0, paddingBottom: statsView && "10px" }}>
                        <Box alignItems="center" gap="20px" flexWrap="wrap" display="flex" justifyContent="space-between" padding='20px' borderBottom="1px solid #d3d3d3">
                            <Box>
                                <Typography variant='h5'>Service Logs</Typography>
                                <Typography color="textSecondary" variant='subtitle2'>Watch all Alive services logs here</Typography>
                            </Box>
                            <Box display="flex" flexWrap="wrap" gap="10px">
                                {!statsView && <Box borderRadius="5px" bgcolor={palette.form.inputBg} border={border[1]} padding="5px 10px" display="flex" gap="5px" alignItems="center">
                                    <Typography variant='subtitle2' display={{ sm: "block", xs: "none" }} >Fetch Count: </Typography>
                                    <IconButton onClick={() => handleCountChange( "-" )} size='small' sx={{ borderRadius: "5px" }}>{Icons.default.RemoveIcon}</IconButton>
                                    <Typography bgcolor={palette.contentBg} padding="5px" borderRadius="5px"><strong>{count}</strong></Typography>
                                    <IconButton onClick={() => handleCountChange( "+" )} size='small' sx={{ borderRadius: "5px" }}>{Icons.default.AddIcon}</IconButton>
                                </Box>}
                                {!statsView && <Button disabled={loadingStats} onClick={() => switchMode( 'stats' )} variant='outlined' color='secondary' sx={{ textTransform: "capitalize" }} startIcon={loadingStats ? <DataLoadingSpinner size={14} padding='0' /> : Icons.default.AnalyticsIconOutlined} >Stats</Button>}
                                {statsView && <Button disabled={loading} onClick={() => switchMode( 'list' )} variant='outlined' color='secondary' sx={{ textTransform: "capitalize" }} startIcon={loading ? <DataLoadingSpinner size={14} padding='0' /> : Icons.default.ViewListIcon} >Logs</Button>}
                                <Button disabled={loading} onClick={refreshData} variant='outlined' color='greyed' sx={{ textTransform: "capitalize" }} startIcon={loading ? <DataLoadingSpinner size={14} padding='0' /> : Icons.default.Replay} >Refresh</Button>
                            </Box>
                        </Box>
                        <FilterBar statsView={statsView} disabled={loading} availableModules={availableModules} />
                        {!error && !loadingStats && statsView && stats.counts && Object.keys( stats.counts ).length > 0 && <Box padding="10px " justifyContent="stretch" display="flex" flexWrap="wrap" gap="20px">
                            <Box flexGrow={1} padding="5px 10px" borderRadius="10px" bgcolor={palette.contentBg} border={`2px solid ${palette.borderColor}`}  >
                                <Typography color="textSecondary" fontSize="12px" variant='subtitle2'>Info Logs</Typography>
                                <Typography fontSize="24px" fontWeight="800"> <em>{stats.counts?.find( item => +item._id === 200 ).count}</em> </Typography>
                            </Box>
                            <Box flexGrow={1} padding="5px 10px" borderRadius="10px" bgcolor={palette.contentBg} border={`2px solid ${palette.borderColor}`}  >
                                <Typography color="textSecondary" fontSize="12px" variant='subtitle2'>Error Logs</Typography>
                                <Typography fontSize="24px" fontWeight="800"> <em>{stats.counts?.reduce( ( sum, item ) => +item._id !== 200 ? sum + item.count : sum, 0 )}</em> </Typography>
                            </Box>
                            {stats.counts?.map( mod => {
                                const statusCode = mod._id
                                return +statusCode !== 200 ? (
                                    <Box minWidth="100px" key={statusCode} flexGrow={1} padding="5px 10px" borderRadius="10px" bgcolor={palette.contentBg} border={`2px solid ${palette.borderColor}`}  >
                                        <Typography color="textSecondary" fontSize="12px" textTransform="capitalize" variant='subtitle2'><em>{statusCode + ""} Error</em></Typography>
                                        <Typography fontSize="24px" fontWeight="800"> <em>{mod.count}</em> </Typography>
                                    </Box>
                                ) : ""
                            } )}
                        </Box>}
                        {statsView && !error && loadingStats && <DataLoadingSpinner waitingMessage={"Loading error counts..."} />}
                    </Card>
                    {!error && <Box overflow="auto" flexGrow={1} justifyContent="space-between" display="flex" flexDirection="column">
                        {/* {!loading && !statsView && filteredLogs.length > 0 && <TableContainer sx={{ overflow: "auto", position: "relative" }}>
                            <Table stickyHeader>
                                <TableHeader columns={tableColumns} />
                                <TableBody>
                                    {filteredLogs.slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( log => {
                                        return (
                                            <LogsTableRow key={log._id} log={log} />
                                        )
                                    } )}
                                    {Math.ceil( filteredLogs.length / rowsPerPage ) <= ( page + 1 ) && <TableRow key="end" sx={{ position: "sticky", borderTop: border[1], top: 44, bgcolor: palette.contentBg, zIndex: 10, }}>
                                        <TableCell onClick={() => getData( true )} sx={{ padding: "5px", cursor: "pointer" }} align="center" colSpan={tableColumns.length}>
                                            <Box display="flex" justifyContent="center" alignItems="center" gap="20px">
                                                {loadingMore ? <DataLoadingSpinner size={14} padding='0' waitingMessage="Loading more logs..." /> : <Typography textAlign="center" ><em>+ Load More</em></Typography>}
                                            </Box>
                                        </TableCell>
                                    </TableRow>}
                                </TableBody>
                            </Table>
                        </TableContainer>}
                        {!statsView && !loading && !error && filteredLogs.length > 0 && <TablePagination
                            sx={{ background: table.headerBg, flexShrink: 0 }}
                            rowsPerPageOptions={[50, 100, 150, 200]}
                            component="div"
                            count={filteredLogs.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={( event, val ) => setPage( val )}
                            onRowsPerPageChange={( event ) => {
                                setRowsPerPage( parseInt( event.target.value, 10 ) )
                                localStorage.setItem( 'adminAuditRowsPerPage', event.target.value )
                                setPage( 0 )
                            }}
                        />} */}
                        {!loading && !statsView && filteredLogs.length > 0 && <Timeline
                            sx={{
                                [`& .${timelineOppositeContentClasses.root}`]: {
                                    flex: 0.05,
                                },
                            }}
                        >
                            {filteredLogs.map( log => (
                                <TimelineItem key={log._id}>
                                    <TimelineOppositeContent fontSize="14px" color="textSecondary">
                                        {formatDate( log.timestamp, { format: "hh:mm A" } )}
                                    </TimelineOppositeContent>
                                    <TimelineSeparator>
                                        {log.meta?.name ? <Avatar sx={{ width: "35px", height: "35px", border: `5px solid white`, textTransform: "uppercase" }}>{Array.from( log.meta?.name?.toLowerCase() ).at( 0 )}</Avatar> : <Avatar sx={{ width: "35px", height: "35px", border: `5px solid white`, textTransform: "uppercase" }}></Avatar>}
                                        <TimelineConnector />
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <LogBlock log={log} />
                                    </TimelineContent>
                                </TimelineItem>
                            ) )}
                        </Timeline>}
                        {!loadingStats && statsView && <Card sx={{ flexShrink: 0, marginTop: "20px" }}>
                            <Typography padding="10px 20px" borderBottom={border[1]} variant='h6'>Errors Count By Module</Typography>
                            <Box flexGrow={1} height="400px" flexShrink={0} padding="20px 0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        width={500}
                                        height={300}
                                        data={stats.errorsByModule}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="shortCode" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="count" fill={palette.error.dark} activeBar={<Rectangle fill={palette.error.main} />} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                            <Box display="flex" padding="0 20px 20px 20px" justifyContent="center" flexWrap="wrap" gap="10px">
                                {stats.errorsByModule.map( item => <Typography fontSize="12px" key={item._id}><strong>{item.shortCode}</strong>-<em>{item.module}</em> </Typography> )}
                            </Box>
                        </Card>}

                        {!statsView && !loading && !error && filteredLogs.length === 0 && < Box alignItems="center" display="flex" gap="10px" flexDirection="column" bgcolor={palette.contentBg} padding="40px 20px"  >
                            <Box component="img" width="180px" src={noLogsImg} />
                            <Typography variant="subtitle2">No logs to show{filtersApplied ? <em> for the applied filters</em> : ""}!</Typography>
                            {filtersApplied && <Button onClick={removeFilters} variant="outlined" color="greyed" sx={{ textTransform: "capitalize" }} startIcon={Icons.default.CloseIcon}>  <em> Remove Filters</em> </Button>}
                        </Box>}
                    </Box>}
                    {( ( loading && !statsView ) || ( statsView && loadingStats ) ) && !error && <DataLoadingSpinner waitingMessage={statsView ? "Generating stats for the logs..." : "Fetching the logs please wait..."} />}
                    {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={getData} />}
                </Paper >
            </Box >
        ) :
            <Box padding="20px" width="100%">
                <BlockMessage message={<span>You doesn't have permission to access this page! please contact <i>Alive co-ordinators</i> for further information.</span>} type="error" />
            </Box>
    )
}

export default DebugLogs
